import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { STALE, unwrap } from "@/lib/query/queryClient";
import { getPatientAccessDetailApi, listIncomingInvitesApi, listMyPatientsApi } from "@/portals/caregiver/api/caregiverApi";
import { getSocket } from "@/lib/socketClient";
import { onResync } from "@/lib/socketRecovery";
import { toastError } from "@/shared/lib/toast";
import type { AppNotification } from "@/stores/notificationsStore";
import type { CaregiverPatientAccessDetail, CaregiverPatientSummary, IncomingCaregiverInvite } from "@/shared/types/caregiverPortal";

// Every notification type that means "something about a patient/relationship
// this caregiver can see just changed on the patient's side" — receiving any
// of these live-refreshes this context instead of waiting for a manual page
// reload (see the socket subscription below).
const RELEVANT_NOTIFICATION_TYPES: ReadonlySet<AppNotification["type"]> = new Set([
  "CAREGIVER_ACCESS_REVOKED",
  "CAREGIVER_ACCESS_UPDATED",
  "ACCESS_CATEGORY_APPROVED",
  "ACCESS_CATEGORY_DECLINED",
  "CAREGIVER_INVITE_ACCEPTED",
  "CAREGIVER_INVITE_CANCELLED",
  "ACCESS_REQUEST_APPROVED",
  "ACCESS_REQUEST_DECLINED",
]);

interface CaregiverPatientsContextValue {
  patients: CaregiverPatientSummary[];
  invites: IncomingCaregiverInvite[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  /** The patient whose shared records are currently displayed on the dashboard. */
  activePatientId: string | null;
  setActivePatientId: (patientId: string | null) => void;
  /** Cached per patient (keyed by patient id) so navigating to a category
   * page and back to the dashboard shows it at once (revalidated quietly). */
  activePatientDetail: CaregiverPatientAccessDetail | null;
  isDetailLoading: boolean;
  /** Call after an action that actually changes what's shared (e.g.
   * requesting more access) to invalidate the cache for the active patient. */
  refetchActivePatientDetail: () => void;
}

const CaregiverPatientsContext = createContext<CaregiverPatientsContextValue | null>(null);

const caregiverKeys = {
  all: ["caregiver"] as const,
  overview: ["caregiver", "overview"] as const,
  /** Prefix for every patient's shared-access detail. */
  details: ["caregiver", "patient"] as const,
  detail: (patientId: string) => ["caregiver", "patient", patientId] as const,
};

const NO_PATIENTS: CaregiverPatientSummary[] = [];
const NO_INVITES: IncomingCaregiverInvite[] = [];

export function CaregiverPatientsProvider({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const [chosenPatientId, setActivePatientId] = useState<string | null>(null);

  // Patients + incoming invites, from the shared query cache (one request for
  // both). Only the very first load shows a spinner; every later refresh
  // swaps the data in place.
  const overview = useQuery({
    queryKey: caregiverKeys.overview,
    queryFn: async () => {
      const [patientsResult, invitesResult] = await Promise.all([listMyPatientsApi(), listIncomingInvitesApi()]);
      const error = patientsResult.error ?? invitesResult.error;
      if (error) throw new Error(error);
      return { patients: patientsResult.data ?? [], invites: invitesResult.data ?? [] };
    },
    staleTime: STALE.records,
  });
  const patients = overview.data?.patients ?? NO_PATIENTS;
  const invites = overview.data?.invites ?? NO_INVITES;
  // The chosen patient while they're still listed, else the first active one.
  const activePatientId =
    chosenPatientId && patients.some((p) => p.id === chosenPatientId)
      ? chosenPatientId
      : patients.find((p) => p.status === "active")?.id ?? null;

  // Cached per patient: going to a category page and back, or switching
  // patients and back, reuses the copy (revalidated quietly).
  const detail = useQuery({
    queryKey: caregiverKeys.detail(activePatientId ?? ""),
    queryFn: () => unwrap(getPatientAccessDetailApi(activePatientId!)),
    enabled: activePatientId !== null,
    staleTime: STALE.records,
  });
  // A failed first load of a patient's detail is toasted once.
  useEffect(() => {
    if (detail.isError && detail.data === undefined) toastError(String(detail.error?.message ?? detail.error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.errorUpdatedAt]);

  const refresh = useCallback(() => void client.invalidateQueries({ queryKey: caregiverKeys.overview }), [client]);
  const refetchActivePatientDetail = useCallback(() => {
    if (activePatientId) void client.invalidateQueries({ queryKey: caregiverKeys.detail(activePatientId) });
  }, [client, activePatientId]);

  // Live: the patient revoked/restored/changed access or resolved an
  // invite/request → the overview and every patient's detail revalidate (the
  // one on screen refetches in place, the rest when next shown). After a
  // reconnect / server resync, everything is revalidated.
  useEffect(() => {
    const socket = getSocket();
    const refreshLive = () => void client.invalidateQueries({ queryKey: caregiverKeys.all });
    const handleNotification = (incoming: AppNotification) => {
      if (RELEVANT_NOTIFICATION_TYPES.has(incoming.type)) refreshLive();
    };
    socket.on("notification:new", handleNotification);
    const offResync = onResync(socket, refreshLive);
    return () => {
      socket.off("notification:new", handleNotification);
      offResync();
    };
  }, [client]);

  const value = useMemo<CaregiverPatientsContextValue>(
    () => ({
      patients,
      invites,
      isLoading: overview.isPending,
      error: overview.isError && !overview.data ? overview.error.message || "Couldn't load your patients. Please try again later." : null,
      refresh,
      activePatientId,
      setActivePatientId,
      activePatientDetail: activePatientId ? detail.data ?? null : null,
      isDetailLoading: activePatientId !== null && detail.isPending,
      refetchActivePatientDetail,
    }),
    [patients, invites, overview.isPending, overview.isError, overview.data, overview.error, refresh, activePatientId, detail.data, detail.isPending, refetchActivePatientDetail],
  );

  return <CaregiverPatientsContext.Provider value={value}>{children}</CaregiverPatientsContext.Provider>;
}

export function useCaregiverPatients() {
  const ctx = useContext(CaregiverPatientsContext);
  if (!ctx) throw new Error("useCaregiverPatients must be used within CaregiverPatientsProvider");
  return ctx;
}
